import {
  resolveApiBase,
  SportsApiClient,
  text,
  toArray,
  type WidgetProps,
} from '@sports/core'
import { useFeed } from '../../use-feed'

export const RACE_CALENDAR_ATTRIBUTES = [
  'sport',
  'limit',
  'heading',
  'refresh',
  'api-base',
  'theme',
] as const

interface RaceCalendarProps extends WidgetProps {
  sport?: string
  limit?: string
  heading?: string
  refresh?: string
  apiBase?: string
}

export const raceCalendarStyles = `
  .race {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.15rem 0.75rem;
    padding: 0.55rem 1rem;
    border-top: 1px solid var(--sports-border);
    font-size: 0.875rem;
    align-items: baseline;
  }
  .race.next { background: color-mix(in srgb, var(--sports-accent) 8%, transparent); }
  .race .date { color: var(--sports-muted); font-size: 0.78rem; white-space: nowrap; }
  .race .gp { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .race .winner {
    grid-column: 1 / -1;
    color: var(--sports-muted);
    font-size: 0.78rem;
  }
  .next-tag {
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--sports-accent);
    margin-left: 0.4rem;
  }
`

/**
 * Season schedule feeds (`f1/f1-results`, `motors/motogp-schedule`) share one
 * shape: `scores.tournament[]` is one Grand Prix carrying per-session blocks,
 * with `race.date` (dd.MM.yyyy) and `race.results.driver[]` once finished.
 */
const SCHEDULE_FEED: Record<string, string> = {
  f1: 'f1-results',
  motogp: 'motogp-schedule',
}

interface RaceEntry {
  id: string
  name: string
  date: string
  dateValue: number
  winner: string
}

function parseDate(value: string): number {
  const [day, month, year] = value.split('.').map(Number)
  if (!year) return Number.NaN
  return Date.UTC(year, (month || 1) - 1, day || 1)
}

function parseSeason(payload: unknown): RaceEntry[] {
  const scores = (payload as { scores?: { tournament?: unknown } })?.scores
  return toArray<Record<string, unknown>>(
    scores?.tournament as Record<string, unknown> | Record<string, unknown>[] | undefined,
  ).map((tournament, index) => {
    const race = tournament.race as Record<string, unknown> | undefined
    const drivers = toArray<Record<string, unknown>>(
      (race?.results as Record<string, unknown> | undefined)?.driver as
        | Record<string, unknown>[]
        | undefined,
    )
    const winner = drivers.find((driver) => text(driver, 'pos') === '1')
    const date = race ? text(race, 'date') : ''
    return {
      id: text(tournament, 'id') || String(index),
      name: text(tournament, 'name'),
      date,
      dateValue: parseDate(date),
      winner: winner ? text(winner, 'name') : '',
    }
  })
}

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export function RaceCalendar({
  sport = 'f1',
  limit,
  heading,
  refresh,
  apiBase,
}: RaceCalendarProps) {
  const max = number(limit, 24)
  // A season schedule changes a few times per weekend, not per minute.
  const refreshSeconds = number(refresh, 300)
  const base = resolveApiBase(apiBase)
  const feedPath = SCHEDULE_FEED[sport] ?? SCHEDULE_FEED.f1

  const { data, error, loading } = useFeed(
    (signal) => new SportsApiClient(base).feed(sport, feedPath, signal),
    [base, sport, feedPath],
    refreshSeconds,
  )

  // A calendar leads with what's ahead: drop Grands Prix already run (they carry
  // a winner) or dated before today, so it never opens on old rounds. Off-season
  // (every round finished) simply shows nothing rather than a list of results.
  const now = new Date()
  const cutoff = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  // Require a real future date: this drops finished rounds AND the dateless
  // "Official Test" placeholder rows GoalServe mixes into the MotoGP schedule,
  // which otherwise floated to the top of the calendar with a blank date.
  const races = (data ? parseSeason(data) : [])
    .filter((race) => !race.winner && !Number.isNaN(race.dateValue) && race.dateValue >= cutoff)
    .sort((a, b) => a.dateValue - b.dateValue)
    .slice(0, max)
  const next = races[0]

  return (
    <div class="sports-card">
      <div class="sports-head">
        <span>{heading ?? (sport === 'motogp' ? 'MotoGP calendar' : 'F1 calendar')}</span>
      </div>

      {loading && <div class="sports-note">Loading…</div>}
      {!loading && error && (
        <div class="sports-note error">The calendar is unavailable right now.</div>
      )}
      {!loading && !error && races.length === 0 && (
        <div class="sports-note">No races found.</div>
      )}

      {races.map((race) => (
        <div class={race === next ? 'race next' : 'race'} key={race.id}>
          <span class="gp">
            {race.name}
            {race === next && <span class="next-tag">next</span>}
          </span>
          <span class="date">{race.date}</span>
          {race.winner && <span class="winner">🏆 {race.winner}</span>}
        </div>
      ))}
    </div>
  )
}
